/**
 * RP4 — Scene bulkLoad effect body extracted from `Scene3DStage.tsx`.
 *
 * Runs the entity-feed → renderable adapter pipeline, hands the result
 * to BatchedLayer, materialises every bucket as a `LineSegments2`
 * mesh, attaches per-entity hatch / wipeout / array-rect / text
 * meshes, refreshes the LOD bucket state, and fits the orthographic
 * camera frustum to the new content.
 *
 * # Why a dedicated module
 *
 * The pre-RP4 effect body lived inline in `Scene3DStage.tsx` and was
 * close to 600 LOC inside one closure. Extracting it gives the
 * orchestrator a single function call per dependency change and lets
 * the helpers (mesh factories, teardown) be imported once at the
 * module level rather than re-bound at every render.
 *
 * Per-pass sub-functions live in `./sceneBulkLoadHelpers.ts`.
 *
 * # Opt 8 — Map consolidation (Phase 2)
 *
 * Pre-consolidation `SceneBulkLoadRefs` carried eight parallel Maps
 * (`meshesRef` / `materialsRef` / `fillMeshesRef` / `textMeshesRef` /
 * `selectionTexturesRef` / `entityToTextureSlotRef` /
 * `bucketHandlesRef` / `lodStateRef`). Now it carries two — `bucketsRef`
 * (per `MeshKeyId`, `BucketState` shape) and `entityRendersRef` (per
 * entity-id, `EntityRender` shape). Helper signatures shrank
 * accordingly.
 */

import * as THREE from "three";

import type {
  RendererHandle,
  RenderableEntity,
} from "../types";
import { type AdapterContext } from "../adapters";
import {
  renderableFromDrawingEntity,
  type TypedEntityResolver,
} from "../adapters/typedFirstRenderable";
import { flattenBlockInsertForRender } from "./liftBlockInsert";
import {
  collectInstancedBlockInserts,
  materializeInstancedBlockInsertMesh,
  type InstancedBlockInsertMesh,
} from "../adapters/blockInsertInstanced";
import { BatchedLayer } from "../batchedLayer";
import { getCoordinateOrigin, setCoordinateOrigin } from "../coordinateOrigin";
import { getEntityMetadataIndex, useEditorStore } from "../../state/editorStore";
import { peekStreamedSpaceIds } from "../../state/mutationStore";
import { recordSceneBulkLoad } from "../../state/importMetrics";
import type {
  BlockDefinition,
  BlockRecord,
  DimensionStyle,
  SavedView,
} from "@dxf-ai-editor/drawing-model";
import { STANDARD_LINETYPES } from "@dxf-ai-editor/drawing-model";
import type { EntityPreview } from "@dxf-ai-editor/contracts";
import type { Scene3DStageProps } from "../Scene3DStageProps";
import {
  buildLinetypeResolutionMap,
  buildLayerLinetypeMap,
  type LinetypeResolutionMap,
  type LayerLinetypeMap,
} from "../rendering/linetypeResolve";
import { type LayerVisibilityState } from "../state/layerVisibility";
import { type VisibleSetCache } from "../state/visibleSetCache";
import {
  buildSpatialRecordsForSpaces,
  buildVisibleRenderables,
  buildVisibleRenderablesChunked,
  createMetadataStubResolver,
  filterAnnotativeHiddenStubs,
  liftRenderableForId,
  reprojectAnnotativeStubBounds,
} from "../state/spatialBuilder";
import {
  disposePointsCloud,
  type VisibleEntityGroups,
  type CameraSnapshot,
} from "../lod";

import {
  disposeSceneBuckets,
  disposeSceneOnUnmount,
  disposeLodCloud,
  type SceneRegistries,
} from "./sceneTeardown";
import { applyAndMirrorLayerVisibility } from "./LayerVisibilityEffect";
import { restampHighlightState } from "./SelectionTextureSync";
import {
  applySavedViewToOrthographicCamera,
  assignMultileaderSatelliteSlots,
  buildBucketMeshes,
  claimAutoFitDocument,
  bucketSpaceClassOf,
  emitFillAndArrayMeshes,
  emitReliftDirectMeshes,
  buildStyleFontMap,
  emitTextMeshes,
  fitOrthographicCameraToRenderables,
  partitionStubsByLod,
  shouldAutoFitCamera,
  type BucketSpaceClass,
  type CameraFitDocState,
} from "./sceneBulkLoadHelpers";
import { planDrawOrderStrataForDrawing } from "./drawOrderPlanForDrawing";
import { expandRenderSatellites, populateLiftedAndBlockChildren } from "./renderSatellites";
import {
  aggregateFitBounds,
  isCacheAuthoritativeSpaceSet,
  resolveRenderSpaceIds,
} from "./cacheDirectSpaces";
import { clearPendingTextRetries } from "./pendingTextRetry";
import type {
  BucketState,
  EntityLiftRequest,
  EntityRender,
} from "./SceneRefsTypes";
import type { MeshKeyId } from "../batchedLayer";

/**
 * Codex P1 #2 (PR #87) — synchronous-lift cap on the cache-direct path.
 * Above this stub count the bulk-load schedules the lift via
 * `buildVisibleRenderablesChunked` so the main thread keeps responsive
 * during a streaming-import post-flush. Below the cap the legacy
 * synchronous lift runs (no behavioural drift for small drawings).
 *
 * 8000 was picked empirically: small drawings (sub-700-entity demo
 * fixtures) all sit well below it and the per-effect lift cost stays
 * <100ms; the П35Р 685k fixture and the 100k smoke fixture both fly
 * over it and take the async path.
 */
const CACHE_DIRECT_ASYNC_LIFT_THRESHOLD = 8000;

/**
 * Codex P2 — cache-direct lift budget cap. Above this count the bulk-load
 * pass refuses to lift every stub into a full RenderableEntity. Without
 * this guard the pre-fix path drove renderer private memory past 8 GB on
 * the P35R 685k fixture (each lift pulls the entity through the preview
 * builder + per-kind adapter, then allocates bucket vertices / GPU
 * buffers downstream) and frequently OOM'd the tab.
 *
 * 100k matches the synthetic-100k fixture exactly so existing tests keep
 * the full-lift code path; the П35Р 685k case hits the budget and lifts
 * only the first BUDGET entities (DRAWORDER prefix — see
 * `buildSpatialRecordsFromMetadata` for the iteration order). Entities
 * beyond the budget remain in the spatial index and metadata cache; the
 * per-frame viewport-culled re-lift pipeline (computeVisibleSet —
 * exported from `lod/visibleSet.ts` but currently not wired into the
 * frame loop) is the follow-up that will surface them on demand as the
 * user pans / zooms.
 */
const CACHE_DIRECT_LIFT_BUDGET = 100_000;

/**
 * Three.js context the bulk-load pass reads from R3F. Captured at
 * effect-time so the helper can run outside the React render fn.
 */
export interface SceneR3FContext {
  scene: THREE.Scene;
  gl: THREE.WebGLRenderer;
  invalidate: () => void;
  size: { width: number; height: number };
  camera: THREE.Camera;
}

/**
 * Inputs the bulk-load pass needs that come from outer Scene3DStage
 * props (entity feed, scale catalogue, block table, geometry doc).
 */
export interface SceneBulkLoadInputs {
  entities: Scene3DStageProps["entities"];
  modelSpaceEntities: Scene3DStageProps["modelSpaceEntities"];
  activeSpaceKind: string;
  /**
   * The DISAMBIGUATED id of the active space (`"model"`, `"layout:Sheet 1"`, …)
   * — `state.drawing.activeSpaceId`, threaded from `Scene3DStage` through
   * `SceneContent` (which already receives it for the pick / drag-window
   * fallback) and `useSceneEffects`.
   *
   * The cache-direct branch builds its spatial records FOR THIS SPACE (plus
   * model space behind a layout — `scene/cacheDirectSpaces.ts
   * resolveRenderSpaceIds`). It is also part of the bulk-load effect's dep
   * identity, so switching layout tabs re-runs the pass for the newly active
   * space; `activeSpaceKind` alone cannot do that (every layout shares the
   * kind `"layout"`) and the streamed feed is the stable EMPTY sentinel.
   */
  activeSpaceId: string;
  blockDefinitions: Scene3DStageProps["blockDefinitions"];
  cannoscaleName: Scene3DStageProps["cannoscaleName"];
  annotationScales: Scene3DStageProps["annotationScales"];
  /** PR-LT3 — drawing LTYPE table for custom-linetype dash resolution. */
  linetypes: Scene3DStageProps["linetypes"];
  /**
   * PR-LT3 variant A (A2) — document linetype sysvars (LTSCALE / MSLTSCALE /
   * PSLTSCALE) for the GLOBAL dash-scale factor. Optional; absent → all-1
   * (pre-A2 behaviour, `dashScale = 1`).
   */
  linetypeSysvars?: Scene3DStageProps["linetypeSysvars"];
  /**
   * @codex #334 (P2-B) — STYLE table entries (name + fontName). Threaded
   * into `emitTextMeshes` so a generically-named STYLE
   * (`name=Standard, fontName=gost.shx`) dispatches on its actual font
   * file, not on the style NAME. Optional; absent/empty → dispatch on
   * `styleName` (the pre-fix behaviour).
   */
  textStyles?: Scene3DStageProps["textStyles"];
  layerStates: ReadonlyArray<LayerVisibilityState>;
}

/**
 * Mutable refs the bulk-load pass owns. Hoisted out of SceneContent so
 * the helper can mutate them in place without crossing a React
 * boundary.
 *
 * Opt 8 — two consolidated Maps replace the eight pre-consolidation
 * `*Refs`. See `SceneRefsTypes.ts` for the field layout of each value.
 */
export interface SceneBulkLoadRefs {
  handleRef: { current: RendererHandle | null };
  batchedLayerRef: { current: BatchedLayer | null };
  visibleSetCacheRef: { current: VisibleSetCache | null };
  bucketsRef: { current: Map<MeshKeyId, BucketState> };
  entityRendersRef: { current: Map<string, EntityRender> };
  textBuildTokenRef: { current: number };
  lockedLayersRef: { current: Set<string> };
  hiddenLayersRef: { current: Set<string> };
  sharedLockedLayersRef: { current: Set<string> };
  prevCameraSnapshotRef: { current: CameraSnapshot | null };
  lodPointsCloudRef: { current: THREE.Points | null };
  /**
   * Codex PR #784 round-12 P2 — the attributed viewport point-proxy clouds
   * (`scene/viewportPointProxies.ts`); torn down with the main cloud.
   */
  lodPointProxyCloudsRef: { current: THREE.Points[] };
  lodGroupsRef: { current: VisibleEntityGroups };
  cameraStateRef: {
    current: { centerX: number; centerY: number; zoom: number };
  };
  /**
   * Issue #225 + #235 follow-up (@codex P2 ×2) — the per-document camera
   * auto-fit claim (`null` before the first bulk-load pass ever). The
   * fit gate (`shouldAutoFitCamera`) reads the PRE-claim value; after
   * the gate has decided, EVERY pass advances it via
   * `claimAutoFitDocument`. `documentId` keys the once-per-document fit
   * (a NEW document loaded in the same session re-fits, while edits —
   * which preserve the id — never re-fit); `contentSeen` defers a
   * streamed document's one fit past its 0-renderable pre-pass to the
   * cache-direct pass that actually materialises content. See the
   * `CameraFitDocState` JSDoc (sceneBulkLoadHelpers) for the full state
   * machine.
   */
  cameraFitDocStateRef: { current: CameraFitDocState | null };
  /**
   * Phase 2 Wave 2 Task 4 — tracker for batched-INSERT InstancedMeshes
   * so they are disposed on the next bulkLoad / unmount via
   * `disposeSceneBuckets` / `disposeSceneOnUnmount`. The array is
   * mutated in place each pass: cleared at the top, repopulated by
   * `materializeInstancedBlockInsertMesh` results.
   */
  instancedBlockMeshesRef: { current: Array<THREE.InstancedMesh> };
  /**
   * Codex P3 follow-up / Item C (2026-05-19) — per-frame viewport-cull
   * re-lift tracking. `runSceneBulkLoad` populates this Set with every
   * entity id it materialises into the BatchedLayer; the
   * `FrameLoops` viewport-cull pass diffs the new
   * `computeVisibleSet` output against this Set to compute the add /
   * remove deltas it issues to `BatchedLayer.addEntity` /
   * `removeEntity`. Cleared at the top of every fresh bulk-load.
   */
  liftedEntityIdsRef: { current: Set<string> };
  /**
   * S9b — parent id (MLEADER with block content / cache-direct INSERT
   * `block-insert` wrapper) → its render-only satellites (`renderSatellites.ts`).
   * `finishSceneBulkLoad` populates it from the un-expanded top-level
   * `renderables`; the FrameLoops viewport-cull pass reads / mutates it to add
   * / remove the satellites in lockstep with the parent (they are absent from
   * the spatial index, so they must NOT be tracked in `liftedEntityIdsRef` —
   * see the ref's JSDoc in `useSceneContentRefs`). Cleared per bulk-load.
   */
  multileaderBlockChildrenRef: { current: Map<string, RenderableEntity[]> };
  /**
   * Codex PR #784 round 3 P1 #2 — revision of `multileaderBlockChildrenRef`,
   * bumped on clear (top of the pass) and repopulate (`finishSceneBulkLoad`,
   * sync AND async paths) — see `useSceneContentRefs` / `spaceLayerCensusCache`.
   */
  renderSatellitesVersionRef: { current: number };
  /**
   * Codex P3 follow-up / Item C (2026-05-19) — on-demand resolver
   * bound to the bulk-load's cache + buildPreview + adapterCtxBase.
   * Returns `null` when the resolver isn't wired (no active
   * bulk-load) or when the id is not present in the spatial index /
   * metadata cache.
   */
  entityResolverRef: {
    /**
     * Codex PR #784 round-11 — the on-demand lift, with an optional REQUEST
     * ({@link EntityLiftRequest}) describing the camera that will render the id
     * and whether a NON-EXPANDING proxy is all the caller wants. Omitting it is
     * the main-camera lift, unchanged.
     *
     * NOTE for the ref's declaration site (`scene/useSceneContentRefs.ts`,
     * `scene/useSceneEffects.ts`): those still type the ref with the 1-argument
     * signature. That is assignable in both directions here (the extra
     * parameter is optional), but the declarations should be widened to this
     * shape so the request contract is visible where the ref is created.
     */
    current: ((id: string, request?: EntityLiftRequest) => RenderableEntity | null) | null;
  };
  /**
   * FIX-3 (Codex P2) — per-pass LTYPE resolution map published at
   * bulk-load time and consumed by the FrameLoops relift path to build a
   * NEW linetype bucket key with the same dash fidelity. Reset to an
   * empty map at the top of every bulk-load.
   */
  linetypeResolutionRef: { current: LinetypeResolutionMap };
  /**
   * FIX-1 (Codex P2) — per-pass per-layer LTYPE map published at bulk-load
   * time and consumed by the FrameLoops relift path so a `ByLayer` bucket it
   * materialises on demand resolves through the layer's assigned linetype.
   * Reset to an empty map at the top of every bulk-load.
   */
  layerLinetypeMapRef: { current: LayerLinetypeMap };
  /**
   * PR-LT3 variant A (A2) — per-pass linetype sysvars (LTSCALE / MSLTSCALE /
   * PSLTSCALE) published at bulk-load time and consumed by the FrameLoops
   * relift path so a bucket it materialises on demand gets the same GLOBAL
   * dash scale as the bulk-loaded buckets. `undefined` → all-1 (pre-A2).
   */
  linetypeSysvarsRef: { current: Scene3DStageProps["linetypeSysvars"] };
  /**
   * Live snapshot of the App's current `state.selection.entityIds`, read at the
   * END of every pass so the freshly built `SelectionStateTexture`s inherit the
   * selection that is already in force (see `restampHighlightState`). A REF
   * rather than a plain input because the async chunked cache-direct path
   * finishes long after the effect body returned and must observe the selection
   * as of that moment, not as of dispatch. Same ref `FrameLoops` reads for its
   * post-relift re-stamp — one snapshot, two consumers.
   */
  selectedEntityIdsRef: { current: readonly string[] | undefined };
}

/**
 * Lookup helpers the cache-direct render path needs to lift a metadata
 * stub into a full RenderableEntity. The orchestrator memoises these
 * on `geometryDocument` identity.
 */
export interface SceneBulkLoadCallbacks {
  buildPreview: (id: string) => EntityPreview | null;
  /**
   * DEFUSE S11 — typed twin of `buildPreview` (see
   * `SceneContentRefs.buildPreviewCallbacks`): resolves the canonical
   * `DrawingEntity` for an id WITHOUT the bag flatten. The typed-first
   * lift dispatches it into `entityToRenderableTyped` /
   * `blockFromEntity`; `buildPreview` stays as the lazy fallback for the
   * not-yet-typed kinds.
   */
  resolveEntity: TypedEntityResolver;
  adapterCtxBase: AdapterContext;
  geometryDocument: {
    /**
     * Issue #225 — document identity used to gate the per-document
     * auto-fit. Required non-empty string on `DrawingDocument`
     * (`drawingDocumentSchema`); distinct per import (fresh
     * `crypto.randomUUID()` in the OPEN reducer) and stable across edits
     * (geometry reducers spread the document, preserving `id`).
     */
    id: string;
    styleTables?: {
      dimensionStyles?: ReadonlyArray<DimensionStyle>;
      /**
       * FIX-1 — layer table; only `name` + `linetype` are read here (the
       * full `LayerDefinition` is a structural superset). Used to build the
       * per-layer LTYPE map so `ByLayer` buckets resolve through the layer's
       * assigned linetype.
       */
      layers?: ReadonlyArray<{ name: string; linetype?: string }>;
    };
    blockRecords?: Record<string, BlockRecord>;
    blocks?: Record<string, BlockDefinition>;
    savedView?: SavedView;
  };
}

/**
 * Run a single bulk-load pass. Returns a cleanup function the React
 * effect should hand back from its body so unmount frees GPU memory
 * cleanly.
 *
 * Callers MUST call the returned cleanup before the next pass; the
 * helper's pre-bulkLoad dispose logic only runs the *current* refs
 * (= identical to the cleanup) so without the React-side return the
 * meshes leak across re-renders.
 */
export function runSceneBulkLoad(
  ctx: SceneR3FContext,
  inputs: SceneBulkLoadInputs,
  refs: SceneBulkLoadRefs,
  cb: SceneBulkLoadCallbacks,
): (() => void) | undefined {
  const _bulkT0 =
    typeof performance !== "undefined" && typeof performance.now === "function"
      ? performance.now()
      : Date.now();
  const recordBulk = (): void => {
    const t1 =
      typeof performance !== "undefined" &&
      typeof performance.now === "function"
        ? performance.now()
        : Date.now();
    recordSceneBulkLoad(t1 - _bulkT0);
  };
  // Codex autocheck finding (2026-05-19, PR #92) — reset the
  // per-frame viewport-cull re-lift tracking at the TOP of every
  // bulk-load pass, not inside `finishSceneBulkLoad`. The previous
  // shape left `liftedEntityIdsRef` populated with the prior pass's
  // entity ids during async cache-direct lift (seconds long on huge
  // drawings). If `FrameLoops` viewport-cull fired during that
  // window it would dispatch stale `removeEntity` / `addEntity`
  // calls against the new spatial index using the OLD lifted-set as
  // the diff base, corrupting BatchedLayer state. Clearing the Set
  // + nulling the resolver at the top of `runSceneBulkLoad` means
  // viewport-cull during the async lift window sees an empty
  // lifted-set + a null resolver — the per-frame branch becomes a
  // no-op until `finishSceneBulkLoad` rewires both refs against the
  // new pass.
  refs.liftedEntityIdsRef.current.clear();
  refs.entityResolverRef.current = null;
  // Codex PR #784 round 2 P3 — the parent → satellites map is maintained in
  // lockstep with the lifted set (`populateLiftedAndBlockChildren`), so it is
  // reset at the same point: a satellite of the PREVIOUS document must not be
  // re-expanded / projected (`satelliteLayerProjection.ts`) during the async
  // lift window before this pass republishes the map.
  refs.multileaderBlockChildrenRef.current.clear();
  refs.renderSatellitesVersionRef.current += 1;
  // FIX-3 — reset the relift LTYPE resolution to an empty map so a stale
  // pass's custom file LTYPEs can't leak into a fresh document's relift.
  // The empty map resolves every non-sentinel name via the mm web registry.
  refs.linetypeResolutionRef.current = new Map();
  // FIX-1 — reset the relift per-layer LTYPE map so a stale pass's layer
  // assignments can't leak into a fresh document's relift. Empty → ByLayer
  // buckets relift as solid until the pass below republishes the real map.
  refs.layerLinetypeMapRef.current = new Map();
  // PR-LT3 variant A (A2) — reset the relift sysvars so a stale pass's
  // LTSCALE can't leak; republished below at the buildBucketMeshes site.
  refs.linetypeSysvarsRef.current = undefined;

  const handle = refs.handleRef.current;
  const batched = refs.batchedLayerRef.current;
  if (!handle || !batched) {
    recordBulk();
    return;
  }
  // Codex P1 #2 (PR #87) — `scene` is used directly for the local
  // cleanup (`disposeSceneOnUnmount`) and for the dispose-/mesh-build
  // pipeline inside `finishSceneBulkLoad`. `gl/invalidate/size/camera`
  // are now read inside `finishSceneBulkLoad` only — no outer
  // destructure needed.
  const { scene } = ctx;
  const { entities, modelSpaceEntities, activeSpaceKind, activeSpaceId } =
    inputs;

  // editorStore.computeCoordinateOriginForImport (PR-A) is the
  // single source of truth for the origin. Scene3DStage only reads
  // it — never computes. A null origin while entities are present
  // means the importer did not call `setCoordinateOrigin` before
  // pushing entities into the scene; that breaks the Float32
  // precision contract on geodetic surveys, so we surface it loudly
  // instead of silently falling back to identity.
  if (entities.length > 0 && getCoordinateOrigin() === null) {
    // PR-V0c hot-fix: draft preview entities (LINE/CIRCLE/PLINE during
    // a draft session) reach Scene3DStage on a *fresh* drawing where
    // no import flow ever ran setCoordinateOrigin. Auto-initialise the
    // origin to (0,0,0) — identity mapping — so the renderer can paint
    // the preview. The PR-A defensive throw was meant for imported
    // documents where missing origin = lost Float32 precision; for an
    // empty/fresh drawing the identity origin is the correct answer.
    setCoordinateOrigin({ x: 0, y: 0, z: 0 });
  }

  const blocksMap = inputs.blockDefinitions ?? {};

  // Build renderables. zOrderIndex preserves DRAWORDER (entity feed
  // order) — BatchedLayer re-sorts by (zOrder, id) inside bulkLoad.
  //
  // PR-V0c — when activeSpaceKind is "layout", model-space entities
  // (passed via `props.modelSpaceEntities`) are appended to the
  // renderable feed AFTER the layout-space entities.
  const sourceEntities: typeof entities =
    activeSpaceKind === "layout" &&
    modelSpaceEntities &&
    modelSpaceEntities.length > 0
      ? [...entities, ...modelSpaceEntities]
      : entities;

  // PR-V2 — cache-direct render path.
  //
  // The spaces this pass renders, in feed order: the ACTIVE space, plus model
  // space behind a paper layout (the same union `sourceEntities` builds above
  // for the legacy branch). See `scene/cacheDirectSpaces.ts`.
  const renderSpaceIds = resolveRenderSpaceIds({
    activeSpaceId,
    activeSpaceKind,
  });
  const metadataIndex = getEntityMetadataIndex();
  // fix/cache-direct-paper-space — the branch predicate is PROVENANCE, not an
  // entity count. `markSpaceStreamed` (`state/mutationStore.ts`) records every
  // space whose inline entities the streaming import discarded, so the compact
  // cache is that space's only complete source; `peekStreamedSpaceIds` reads it
  // back for THIS document (`geometryDocument.id` is the holder key —
  // `state/editorStoreImportFileActions.ts` calls `setActiveDocument` with the
  // same id it commits).
  //
  // The count test alone cannot see this: a streamed paper layout retains
  // exactly one inline entity, its VIEWPORT, so `sourceEntities.length === 0`
  // read FALSE for every layout of a >=2 MB import and the pass fell to the
  // legacy branch — which then rendered that lone Viewport and nothing else.
  // The `length === 0` clause is kept as the second sufficient condition: a
  // feed with nothing in it can never be the authority for a non-empty index
  // (this is the model-space streaming case that has always taken this branch).
  const useCacheDirect =
    metadataIndex.size > 0 &&
    (isCacheAuthoritativeSpaceSet(
      renderSpaceIds,
      peekStreamedSpaceIds(cb.geometryDocument.id),
    ) ||
      sourceEntities.length === 0);

  const renderables: RenderableEntity[] = [];
  /**
   * @codex PR #785 round 13, P1 — the WHOLE drawing in DRAWORDER, kept so the
   * strata plan can be cut from it rather than from the entities that survived
   * the LOD pre-classify. A dropped wide polyline is relifted by `FrameLoops`
   * Pass 2 long after this pass, and a plan cut from the survivors has no
   * boundary where it is authored (`scene/drawOrderPlanForDrawing.ts`).
   * `undefined` on the legacy branch, whose feed IS the whole drawing.
   */
  let drawOrderStubs: readonly RenderableEntity[] | undefined;
  // Phase 2 Wave 2 Task 4 — buffer for materialised InstancedMeshes from
  // INSERT batching. Hoisted out of the `else` branch (F1 dispose-order
  // fix) so the attach loop below `disposeSceneBuckets` can read it.
  // Stays empty on the cache-direct path because that path skips the
  // adapter pipeline entirely.
  const materializedInstancedMeshes: InstancedBlockInsertMesh[] = [];
  // Codex P1 #2 (PR #87) — async chunked lift token. Created lazily
  // when the cache-direct branch dispatches the chunked path; null
  // otherwise so the synchronous cleanup is a no-op for normal flows.
  let asyncLiftAbort: { aborted: boolean } | null = null;
  if (useCacheDirect) {
    // Cache-direct path. Build the metadata-stub list for spatial
    // index population; defer per-id RenderableEntity construction
    // to the LOD viewport-cull pass, which queries the spatial index
    // for visible ids and lifts each through the cache.
    const cache = refs.visibleSetCacheRef.current;
    if (!cache) return;
    cache.clear();

    // S10d round-21 (@codex, SceneBulkLoad:464) — drop annotative stubs that are
    // HIDDEN at the active scale (ANNOALLVISIBLE=false, no representation / not an
    // OBJECTSCALE member) BEFORE the spatial index + fit/LOD pre-classify consume
    // them; otherwise a far-away hidden annotation stays pickable in the rbush and
    // distorts Zoom Extents / LOD bucketing even though its later lift returns
    // null. Same `typedAnnotativeHidden` truth the render gate + pick path use. A
    // no-active-scale / non-annotative document keeps the array unchanged.
    const stubs = filterAnnotativeHiddenStubs({
      stubs: buildSpatialRecordsForSpaces(metadataIndex, renderSpaceIds),
      resolveEntity: cb.resolveEntity,
      ctx: cb.adapterCtxBase,
    });
    // S10d round-19 (@codex, textFromEntity:83) — the metadata bounds are the
    // MASTER insertion point, but an annotative TEXT/MTEXT renders at its
    // per-scale `anno.insertion`. Reproject the (visible) stubs onto the
    // active-scale position so the spatial index returns them as pick /
    // window-select candidates at their DISPLAYED location, not the stale master
    // point. The adapters return OFFSET-world bounds and the stub array is
    // OFFSET-world too, so the reproject writes them verbatim — one frame.
    reprojectAnnotativeStubBounds({
      stubs,
      resolveEntity: cb.resolveEntity,
      ctx: cb.adapterCtxBase,
    });
    // The plan is cut from THIS array (every stub, lifted or not) — see
    // `drawOrderStubs`' declaration.
    drawOrderStubs = stubs;
    handle.spatialIndex.bulkLoad(stubs);
    // #589 — ONE FRAME. The spatial index is ALWAYS offset-world: it is a
    // derived representation of the renderer's scene graph, so it lives in
    // the scene graph's frame, on this branch exactly as on the legacy one
    // below. `buildSpatialRecordsForSpaces` converts the raw
    // `EntityMetadataIndex` box at its single seam (see its "Coordinate
    // frame" doc); `EntityMetadataIndex` itself stays RAW because it is the
    // document store, not a render derivative. Declared explicitly all the
    // same, so a consumer that needs the true AutoCAD-world coordinate (e.g.
    // the test bridge's entityWorldBounds()) reads a signal instead of
    // guessing from index size.
    //
    // This used to declare "raw-world", while every query the renderer made
    // — the per-frame LOD re-lift, pick, window-select, OSNAP — was
    // offset-world. Under a non-zero locked origin the two frames differed by
    // the whole origin and a survey-scale drawing rendered as a blank canvas.
    handle.spatialIndex.setBoundsFrame?.("offset-world");

    // Codex P3 — pre-classify stubs by LOD bucket against the initial
    // fit-to-extents zoom. Stubs already carry per-entity bounds (see
    // `metadataToSpatialStub`), so we can compute on-screen pixel size
    // without lifting the full RenderableEntity. Entities whose bucket
    // falls into `"skip"` (sub-0.5 px) get dropped from the lift list:
    //   * they are invisible to the user at the initial fit zoom
    //   * even when lifted they would not emit user-perceivable pixels
    //   * lifting them allocates a full RenderableEntity + per-kind
    //     geometry + bucket vertex slots — wasted on entities the
    //     renderer would draw as 0–1 px specks
    //
    // TEXT / MTEXT EXEMPTION: text has a small world bbox, so at the tiny
    // fit-to-extents `fitZoom` of a large drawing it ALL buckets as "skip"
    // below. The per-frame viewport-cull re-lift (FrameLoops, Item C
    // 2026-05-19) does NOT recover a dropped text stub: it only feeds
    // BatchedLayer line geometry, and `flattenForBucket` returns [] for
    // text/mtext — text meshes are built ONLY by `emitTextMeshes` at
    // bulk-load, which the re-lift never re-invokes. So a dropped text stub
    // would never reach `emitTextMeshes` for the WHOLE session =
    // permanently invisible labels (the reported bug). Hence
    // `partitionStubsByLod` always lifts text-like stubs; the LOD lift-drop
    // no longer hides them (a separate PR-V5 height gate still applies at
    // emit but clears at the streamed `v5Zoom = 1`).
    //
    // NON-exempt dropped entities ARE re-lifted on zoom-in (Codex PR #784
    // round-8 P2 — this used to be a permanent drop). The per-frame
    // viewport-cull re-lift in `FrameLoops` (Pass 2) diffs
    // `groups.full ∪ groups.bboxLine` (from `computeVisibleSet`,
    // lod/visibleSet.ts) against `liftedEntityIdsRef` and calls
    // `entityResolverRef` → `batchedLayer.addEntity` for the newly-visible ids.
    // `computeVisibleSet` resolves every rbush hit through the spatial index's
    // entity resolver and drops ids that resolve to `undefined` — so
    // `finishSceneBulkLoad` chains a METADATA-STUB FALLBACK behind the lifted
    // `renderableById` map, and the on-demand lift publishes the real
    // renderable + emits its direct meshes. A stub dropped here is therefore
    // recoverable the first frame the camera makes it big enough to draw.
    // The remaining `isAlwaysLiftedStubType` exemptions (TEXT / MTEXT /
    // DIMENSION / MULTILEADER) are kept for the reasons documented there, not
    // because the drop would be permanent.
    //
    // Compute the initial fit-to-extents zoom from aggregate stub
    // bounds + the live canvas size. Mirrors `fitBoundsToViewport`
    // (cameraApi.ts) modulo the padding factor — we don't need the
    // padded zoom here because the classifier is consulted before
    // the camera is actually fitted (and the camera-fit gate above
    // applies its own padding when it fires).
    //
    // The box is the ACTIVE space's stubs (`aggregateFitBounds`) — on a paper
    // layout the main camera frames the SHEET, and unioning the model extent
    // behind it would classify every paper entity sub-pixel. Model stubs are
    // measured per VIEWPORT instead (`viewports/viewportLiftRequests.ts`).
    const fitBounds = aggregateFitBounds(stubs, activeSpaceId);
    const aggW = fitBounds.maxX - fitBounds.minX;
    const aggH = fitBounds.maxY - fitBounds.minY;
    const canvasW = Math.max(1, ctx.size.width);
    const canvasH = Math.max(1, ctx.size.height);
    const fitZoom =
      Number.isFinite(aggW) && Number.isFinite(aggH) && aggW > 0 && aggH > 0
        ? Math.min(canvasW / aggW, canvasH / aggH)
        : 1;

    // Filter by LOD bucket — drop only non-text "skip" (sub-0.5 px).
    // Keep "point", "bbox-line", and "full" so the user still sees the
    // small entities at fit-to-extents (the renderer's existing adapter
    // pipeline + points cloud handle those buckets without additional
    // integration). TEXT / MTEXT are always kept (see the exemption note
    // above) — `partitionStubsByLod` encapsulates both rules.
    const { liftable: liftableStubs, droppedSkipCount } = partitionStubsByLod(
      stubs,
      fitZoom,
    );

    // Codex P2 follow-up (autocheck finding 2026-05-18) — DROP the
    // hard-budget truncation. The earlier slice-to-BUDGET strategy
    // truncated `visibleIds` to the first BUDGET entries while the
    // spatial index still held all stubs; `finishSceneBulkLoad`
    // builds its resolver from `renderables` only, so ids beyond the
    // cap resolved to `undefined` and the entities were silently
    // non-rendered AND non-pickable. Users of any drawing above the
    // cap saw only a DRAWORDER-prefix of entities and the rest
    // disappeared from interaction surfaces.
    //
    // P3's LOD pre-classify above already drops sub-pixel "skip"
    // entities at fit-to-extents zoom, which on huge drawings (685k)
    // typically reduces the liftable set to a fraction of the raw
    // stub count. That filter is semantically correct (entities the
    // renderer cannot show at this zoom) where the budget was a
    // blunt count cap. Combined with P5/Option-B drain (entity
    // payloads GC during bridge) the lift cost stays bounded
    // without arbitrary truncation.
    //
    // The budget constant is retained as a soft observability
    // threshold — exceeding it logs a warning but does NOT truncate.
    // A follow-up PR wiring per-frame viewport-culled re-lift via
    // `computeVisibleSet` (lod/visibleSet.ts) will replace the bulk
    // lift with on-demand viewport queries.
    const visibleIds = liftableStubs.map((s) => s.id);
    if (droppedSkipCount > 0 || liftableStubs.length > CACHE_DIRECT_LIFT_BUDGET) {
      console.log(
        `[SceneBulkLoad] cache-direct lift: ${stubs.length} stubs at ` +
          `fitZoom=${fitZoom.toFixed(4)} → ${liftableStubs.length} liftable ` +
          `(${droppedSkipCount} dropped as "skip" by LOD pre-classify)` +
          (liftableStubs.length > CACHE_DIRECT_LIFT_BUDGET
            ? `; soft-threshold ${CACHE_DIRECT_LIFT_BUDGET} exceeded — viewport-culled re-lift wiring is the follow-up`
            : ""),
      );
    }
    // Codex P1 #2 (PR #87) — switch to the async chunked lift when
    // there are too many stubs to lift synchronously without hanging
    // the main thread. The synchronous variant takes ~5s on 100k
    // entities (each `liftRenderableForId` runs the preview builder +
    // the per-kind adapter) and OOM'd / hung the tab on the 685k П35Р
    // fixture. The threshold is set high enough that small drawings
    // keep the synchronous code path (no behavioural drift) and only
    // huge imports take the chunked path.
    if (visibleIds.length > CACHE_DIRECT_ASYNC_LIFT_THRESHOLD) {
      // Async path: defer lift + bulkLoad + mesh-build to an IIFE so
      // the effect body can return a cleanup synchronously. The IIFE
      // checks the abort flag before each chunk and skips the
      // downstream batch / mesh-build steps if cancelled.
      asyncLiftAbort = { aborted: false };
      const abortRef = asyncLiftAbort;
      // Capture every register the IIFE needs so the closure does not
      // re-read `refs.*` at later times (the React-driven cleanup may
      // mutate them between chunks; we want a consistent snapshot).
      const ctxCapture = ctx;
      const refsCapture = refs;
      const cbCapture = cb;
      const inputsCapture = inputs;
      const handleCapture = handle;
      const batchedCapture = batched;
      const materializedCapture = materializedInstancedMeshes;
      const renderablesCapture = renderables;
      const drawOrderStubsCapture = drawOrderStubs;
      void (async () => {
        // Codex round 2 #6 (PR #87) — wrap the IIFE body in try/catch
        // so a throw from `buildVisibleRenderablesChunked` (malformed
        // entity data during adapter lift) or `finishSceneBulkLoad`
        // (bucket-build / mesh-attach failure) does not become an
        // unhandled promise rejection. The synchronous path lets
        // errors bubble out of `runSceneBulkLoad` to the React effect
        // (which surfaces them through the error boundary); the async
        // path has no such caller because the effect already returned
        // its cleanup. So we have to log + signal + clean up locally.
        //
        // Codex round-5 #12 — `refsCapture.*Ref.current` are LIVE
        // shared registries. If this pass is stale by the time it
        // throws (the effect was re-run, cleanup disposed our state,
        // and a newer pass repopulated the refs), disposing
        // `refsCapture.*Ref.current` would tear down the newer pass's
        // buckets/meshes and blank the live scene. Snapshot the
        // registry objects (Map / Array references) at IIFE entry;
        // on throw, only dispose if `refsCapture.*Ref.current` still
        // points to those exact objects — meaning no newer pass has
        // swapped them out. The synchronous cleanup path (line ~450
        // below) already runs before any newer pass mounts, so it
        // remains safe.
        const passBuckets = refsCapture.bucketsRef.current;
        const passEntityRenders = refsCapture.entityRendersRef.current;
        const passInstancedBlockMeshes =
          refsCapture.instancedBlockMeshesRef.current;
        try {
          const lifted = await buildVisibleRenderablesChunked({
            visibleIds,
            cache,
            buildPreview: cbCapture.buildPreview,
            resolveEntity: cbCapture.resolveEntity,
            ctxBase: cbCapture.adapterCtxBase,
            chunkSize: 2000,
            isAborted: () => abortRef.aborted,
          });
          if (lifted === null) return;
          for (const r of lifted) renderablesCapture.push(r);
          if (abortRef.aborted) return;
          finishSceneBulkLoad({
            ctx: ctxCapture,
            inputs: inputsCapture,
            refs: refsCapture,
            cb: cbCapture,
            handle: handleCapture,
            batched: batchedCapture,
            renderables: renderablesCapture,
            materializedInstancedMeshes: materializedCapture,
            useCacheDirect: true,
            drawOrderStubs: drawOrderStubsCapture,
          });
          recordBulk();
        } catch (err) {
          // 1) Operational diagnosis: log to console with full error.
          //    Do NOT swallow — silent failure makes the live-smoke
          //    path's `canvasCount=1` check pass while the user sees
          //    nothing.
          console.error(
            "[SceneBulkLoad] async cache-direct path failed:",
            err,
          );
          // Codex autocheck (2026-05-18) — snapshot `abortRef.aborted`
          // BEFORE setting it on the next line. The catch is reached
          // both via React-cleanup-triggered abort (newer pass mounted)
          // AND via genuine downstream errors. Only the former means a
          // newer pass owns the registries; we must distinguish the
          // two so the dispose branch below skips when this pass is
          // stale and proceeds when this pass owns the state.
          const wasAbortedBeforeCatch = abortRef.aborted;
          // 2) Mark this pass aborted so any in-flight chunk callbacks
          //    no-op and the post-render bucket-build is skipped.
          abortRef.aborted = true;
          // 3) Clean up any partial bucket state. The async path may
          //    have attached InstancedMeshes from `materializedCapture`
          //    before the throw; dispose them via the registry walker
          //    that the sync cleanup uses.
          //
          //    Codex round-5 #12 + autocheck follow-up (2026-05-18) —
          //    use the abort flag (set by the React effect cleanup
          //    when a newer pass mounts) as the stale-pass discriminator.
          //
          //    The earlier identity check (liveBuckets === passBuckets
          //    AND peers) was unreliable because the per-pass refs are
          //    long-lived `useRef` Map/Array instances that are mutated
          //    and cleared IN PLACE rather than swapped — `.current`
          //    keeps the same object identity across reruns. If a newer
          //    pass had already cleared and repopulated the registries
          //    in place, this branch would still see "same identity"
          //    and dispose the LIVE state, blanking the new scene.
          //
          //    `abortRef.aborted` is set by the synchronous cleanup
          //    return value the moment React unmounts / reruns this
          //    effect (the cleanup runs strictly before any newer
          //    effect body fires), so by the time the async IIFE
          //    reaches this catch, `aborted === true` is the
          //    authoritative "a newer pass is now in charge" signal.
          try {
            if (wasAbortedBeforeCatch) {
              console.warn(
                "[SceneBulkLoad] stale async pass — skipping dispose " +
                  "(newer pass mounted before this pass threw)",
              );
            } else {
              const registries: SceneRegistries = {
                buckets: passBuckets,
                entityRenders: passEntityRenders,
                instancedBlockMeshes: passInstancedBlockMeshes,
              };
              disposeSceneBuckets(scene, registries);
            }
          } catch (disposeErr) {
            // Belt-and-suspenders — never let cleanup re-throw out of
            // the async IIFE. The original `err` is the meaningful
            // signal; a dispose failure is a secondary effect.
            console.error(
              "[SceneBulkLoad] async cleanup failed:",
              disposeErr,
            );
          }
          // 4) Signal failure explicitly. Codex round 3 #7 (PR #87):
          //    the previous reset path wrote `{phase: "sync", percent:
          //    100, active: false}` which is the success shape — an
          //    operator inspecting `importProgress` could not distinguish
          //    a failed render from a clean post-import settle.
          //
          //    Replace the success-shaped reset with two error
          //    signals:
          //      (a) clear `importProgress` to the initial idle shape
          //          (`phase: null, percent: 0, active: false`) — this
          //          dismisses any in-flight ImportProgressDialog
          //          without falsely flagging completion.
          //      (b) stash the error on `globalThis.__react_error_capture`
          //          (same global the ErrorBoundary writes to) so the
          //          live-smoke harness's existing capture path picks
          //          it up. Without this the harness's `canvasCount=1`
          //          check would still PASS while the user sees a blank
          //          stage — exactly the masquerade Codex flagged.
          try {
            useEditorStore.setState((s) => ({
              ...s,
              importProgress: {
                phase: null,
                percent: 0,
                itemsProcessed: 0,
                itemsTotal: 0,
                active: false,
              },
            }));
          } catch {
            // best-effort — store setState should never throw, but
            // never let the error path itself crash.
          }
          try {
            const message = err instanceof Error ? err.message : String(err);
            const stack = err instanceof Error ? err.stack : undefined;
            (
              globalThis as { __react_error_capture?: unknown }
            ).__react_error_capture = {
              message: `[SceneBulkLoad] async cache-direct path failed: ${message}`,
              componentStack: "(SceneBulkLoad async IIFE — no React stack)",
              stack,
              capturedAt: new Date().toISOString(),
              source: "SceneBulkLoad",
            };
          } catch {
            // best-effort — never let the error path itself crash.
          }
          // Record the bulk metric anyway so the perf telemetry
          // captures the failed attempt — silent skip would mask
          // pattern of failures from operator review.
          recordBulk();
        }
      })();
      // Sync cleanup runs immediately; downstream mesh-build is deferred.
      return (): void => {
        if (asyncLiftAbort) asyncLiftAbort.aborted = true;
        // The deferred path may not have created bucket meshes yet, but
        // we still must dispose any previous-pass state. The dispose
        // helpers walk `refs.*Ref.current` which is the live registry —
        // they are safe to call even when this pass added no meshes.
        const registries: SceneRegistries = {
          buckets: refs.bucketsRef.current,
          entityRenders: refs.entityRendersRef.current,
          instancedBlockMeshes: refs.instancedBlockMeshesRef.current,
        };
        disposeSceneOnUnmount(scene, registries);
        refs.textBuildTokenRef.current += 1;
        // Codex PR #784 round-11 P1 — the token bump invalidates every text build
        // of this pass; the pending-retry registry indexes exactly those runs, so
        // it is emptied in lockstep (`scene/pendingTextRetry.ts`).
        clearPendingTextRetries(refs.entityRendersRef.current);
        disposeLodCloud(
          scene,
          refs.lodPointsCloudRef,
          refs.lodGroupsRef,
          refs.prevCameraSnapshotRef,
          refs.lodPointProxyCloudsRef,
        );
      };
    }
    // Synchronous path: small drawing — lift everything up-front.
    const lifted = buildVisibleRenderables({
      visibleIds,
      cache,
      buildPreview: cb.buildPreview,
      resolveEntity: cb.resolveEntity,
      ctxBase: cb.adapterCtxBase,
    });
    for (const r of lifted) renderables.push(r);
  } else {
    // Phase 2 Wave 2 Task 4 — pre-pass: group identical INSERTs into
    // batched groups for InstancedMesh rendering. Below-threshold
    // groups (count < INSTANCED_INSERT_MIN_COUNT) and non-INSERT
    // entities flow through the existing per-entity adapter loop via
    // the `passthrough` list. The grouping operates on `EntityPreview`
    // (the input feed shape) so we group BEFORE flatten — flattening
    // every passthrough INSERT is still required for the per-instance
    // children.
    const { groups: instancedGroups, passthrough: groupedPassthrough } =
      // @codex round-20 (blockInsertInstanced:262) — thread the typed resolver so
      // an INSERT carrying a non-empty OBJECTSCALE allow-list (`annotationScales`,
      // absent from the preview bag) is kept OFF the instanced path and resolves
      // its per-scale visibility through `blockFromEntity`.
      // S10e — also thread `blocksMap` so a dynamic-block INSERT (its
      // definition carries `dynamicMetadata`) is kept OFF the instanced path
      // too and resolves its per-instance `evaluateBlock` state through
      // `blockFromEntity` (see `isDynamicBlockInsert` doc comment).
      collectInstancedBlockInserts(sourceEntities, cb.resolveEntity, blocksMap);
    // Materialise each group as one or more `THREE.InstancedMesh`
    // objects. Failures (unresolved block name, line-content block per
    // F2 fallback, empty group) come back as empty arrays — in those
    // cases the group's inserts are routed back through the per-INSERT
    // flatten passthrough below so they render correctly.
    //
    // F2 (codex P1) — line-content blocks return `[]` from the
    // materializer because `THREE.InstancedMesh` renders triangle
    // topology and our shared geometry is gl.LINES vertex pairs.
    // `passthroughForGroup` accumulates the inserts that need to fall
    // back; we splice them into the per-entity loop below, preserving
    // their original feed indices so DRAWORDER is honoured.
    const fallbackInserts: EntityPreview[] = [];
    for (const group of instancedGroups) {
      const materialised = materializeInstancedBlockInsertMesh(
        group,
        blocksMap,
        cb.adapterCtxBase,
      );
      if (materialised.length === 0) {
        for (const ins of group.inserts) fallbackInserts.push(ins);
        continue;
      }
      for (const m of materialised) materializedInstancedMeshes.push(m);
    }

    // Merge fallback inserts back into the passthrough list by their
    // original feed index, so DRAWORDER is preserved across the union.
    // `sourceEntities` is the authoritative order; we walk it once and
    // emit each entity from either the grouper passthrough or the
    // fallback list (a Set lookup of fallback ids resolves O(1)).
    let passthrough: typeof groupedPassthrough = groupedPassthrough;
    if (fallbackInserts.length > 0) {
      const fallbackIds = new Set(fallbackInserts.map((e) => e.id));
      const groupedPassthroughIds = new Set(groupedPassthrough.map((e) => e.id));
      const merged: EntityPreview[] = [];
      for (const e of sourceEntities) {
        if (fallbackIds.has(e.id) || groupedPassthroughIds.has(e.id)) {
          merged.push(e);
        }
      }
      passthrough = merged;
    }

    // F3 (codex P1 #3, post-cycle-1) — Build an id → original-feed-index
    // map ONCE so each passthrough entity can be assigned its true
    // DRAWORDER position (the index it occupied in `sourceEntities`),
    // not the compressed index in the post-grouping passthrough array.
    // Previously we used `i` (the passthrough loop index) as
    // `zOrderIndex` — after a batched group removed K identical INSERTs
    // from the feed, every entity after them shifted up by K positions
    // in `passthrough`, so their painted z-order silently advanced ahead
    // of entities they were supposed to render BEHIND. BatchedLayer
    // re-sorts on `(zOrder, id)` so the bug shows up as wrong stacking
    // (hatches painted over text, dim lines hidden by fill geometry, ...).
    const feedIndexForZOrder = new Map<string, number>();
    for (let i = 0; i < sourceEntities.length; i++) {
      feedIndexForZOrder.set(sourceEntities[i]!.id, i);
    }

    for (let i = 0; i < passthrough.length; i++) {
      const entity = passthrough[i];
      if (!entity) continue;
      const originalFeedIndex = feedIndexForZOrder.get(entity.id) ?? i;
      const ctxAdapter = {
        ...cb.adapterCtxBase,
        zOrderIndex: originalFeedIndex,
      };

      // PR-F / Lane H (S8+S9): dispatch block via its specialised flatten;
      // everything else — INCLUDING Dimension and Multileader now that both
      // have typed adapters — routes typed-first (DEFUSE S11) through
      // `renderableFromDrawingEntity` below, with the legacy bag table as
      // the not-yet-typed fallback for the remaining kinds. Dimension
      // resolves via `entityToRenderableTyped`'s switch; Multileader via
      // the explicit branch in `renderableFromDrawingEntity` itself (see
      // `typedFirstRenderable.ts`'s `EXPLICIT_DISPATCH_RENDER_KINDS` doc —
      // block-content flatten needs the shared dispatcher internally, so
      // Multileader can't be IN that dispatcher's own switch without an
      // import cycle). `cb.adapterCtxBase` (== `ctxAdapter` below) carries
      // the DIMSTYLE table / block table / annotation scale both typed
      // adapters need (`SceneContent.tsx`'s `adapterCtxBase` memo).
      if (entity.type === "Block") {
        // DEFUSE S11 cutover — the INSERT flattens through the TYPED
        // `blockFromEntity` (plan §4.3 deletion-coupling): resolve the
        // canonical `BlockInsertEntity` and flatten typed children
        // directly, no `buildShellEntityPreview` bag round-trip. The
        // legacy amplifier is deleted. `./liftBlockInsert.ts` is the ONE
        // render call site (cache-direct wraps the SAME result as satellites).
        const typedInsert = cb.resolveEntity(entity.id);
        if (typedInsert && typedInsert.type === "Block") {
          for (const f of flattenBlockInsertForRender(typedInsert, blocksMap, ctxAdapter))
            renderables.push(f);
        } else if (typeof console !== "undefined") {
          // A feed preview whose id does not resolve to a typed INSERT
          // should be impossible (the feed derives from the same
          // document) — warn instead of silently dropping so a resolver
          // regression is visible.
          console.warn(
            `[SceneBulkLoad] Block preview '${entity.id}' did not resolve ` +
              "to a typed BlockInsertEntity — INSERT skipped",
          );
        }
        continue;
      }

      const renderable = renderableFromDrawingEntity({
        typed: cb.resolveEntity(entity.id),
        // The feed entity IS the already-built bag preview — reuse it for
        // the fallback kinds instead of re-flattening.
        buildPreview: () => entity,
        ctx: ctxAdapter,
      });
      if (renderable) renderables.push(renderable);
    }

    // F4 (codex P2) — register a synthetic `block-insert` RenderableEntity
    // per grouped INSERT so the spatial index sees its bounds and the
    // entity participates in pick / window-select / fit-to-extents /
    // layer-visibility. Without this stub the InstancedMesh renders the
    // geometry but the underlying entity ids are invisible to every
    // entity-id-keyed registry (selection / hover / layer toggling all
    // miss them). zOrder uses the original feed index recoverable via
    // a Map<id, index> built once below.
    if (materializedInstancedMeshes.length > 0) {
      // Reuse `feedIndexForZOrder` built above instead of rebuilding the
      // same Map (F3 codex P1 #3 fix consolidates both call sites).
      for (const item of materializedInstancedMeshes) {
        for (const ib of item.instanceBounds) {
          const sourceEntity = sourceEntities.find((s) => s.id === ib.id);
          const layer = sourceEntity?.properties["Layer"] ?? "0";
          const renderable: RenderableEntity = {
            id: ib.id,
            spaceId: sourceEntity?.spaceId ?? "model",
            type: "Block",
            bounds: ib.bounds,
            layer,
            colorHex: "#ffffff",
            lineweightWorld: 0,
            // PR-LT3 — block-insert wrapper carries the source INSERT's
            // group-6 linetype; default ByLayer when absent.
            linetype:
              sourceEntity?.properties["Linetype"]?.trim() || "ByLayer",
            zOrder: feedIndexForZOrder.get(ib.id) ?? 0,
            geometry: {
              kind: "block-insert",
              blockName: item.blockName,
              insertion: { x: 0, y: 0, z: 0 },
              scale: { x: 1, y: 1, z: 1 },
              rotation: 0,
              childRenderables: [],
            },
          };
          renderables.push(renderable);
        }
      }
    }

    handle.spatialIndex.bulkLoad(renderables);
    // #589 — the legacy/common path's renderables are OFFSET-world (every
    // geometry adapter applies coordinateOrigin's applyOffset — see
    // sceneGeometryBuilders.ts), the SAME frame the cache-direct branch above
    // declares. See that branch's comment for the full contract.
    handle.spatialIndex.setBoundsFrame?.("offset-world");
  }
  const registries = finishSceneBulkLoad({
    ctx,
    inputs,
    refs,
    cb,
    handle,
    batched,
    renderables,
    materializedInstancedMeshes,
    useCacheDirect,
    drawOrderStubs,
  });

  recordBulk();

  // Cleanup: dispose meshes/materials on unmount or before the next
  // bulkLoad (the early section of this effect handles the latter).
  // Note: `asyncLiftAbort` is always `null` on this synchronous branch
  // (the async cache-direct path returns its own cleanup higher up
  // before flow reaches here).
  void asyncLiftAbort;
  return (): void => {
    disposeSceneOnUnmount(scene, registries);
    refs.textBuildTokenRef.current += 1;
    // Codex PR #784 round-11 P1 — the token bump invalidates every text build
    // of this pass; the pending-retry registry indexes exactly those runs, so
    // it is emptied in lockstep (`scene/pendingTextRetry.ts`).
    clearPendingTextRetries(refs.entityRendersRef.current);
    disposeLodCloud(
      scene,
      refs.lodPointsCloudRef,
      refs.lodGroupsRef,
      refs.prevCameraSnapshotRef,
      refs.lodPointProxyCloudsRef,
    );
  };
}

/**
 * Codex P1 #2 (PR #87) — post-renderables-build stage of
 * `runSceneBulkLoad`. Extracted so the async chunked cache-direct path
 * can call it AFTER the chunked lift completes (`buildVisibleRenderablesChunked`
 * resolves), while the synchronous (small-drawing) path still calls it
 * inline after building `renderables` up-front.
 *
 * Side-effects: populates `batched`, materialises bucket meshes, attaches
 * InstancedMeshes, emits fill / array / text meshes, rebuilds the
 * entity → slot lookup, applies the camera fit-to-extents latch,
 * applies layer visibility, and triggers a paint via `invalidate()`.
 *
 * Returns the `SceneRegistries` snapshot the outer cleanup uses.
 */
function finishSceneBulkLoad(args: {
  ctx: SceneR3FContext;
  inputs: SceneBulkLoadInputs;
  refs: SceneBulkLoadRefs;
  cb: SceneBulkLoadCallbacks;
  handle: RendererHandle;
  batched: BatchedLayer;
  renderables: RenderableEntity[];
  materializedInstancedMeshes: InstancedBlockInsertMesh[];
  /**
   * Forwarded from `runSceneBulkLoad` so the auto-fit gate at the
   * bottom of this function can keep firing on huge cache-direct
   * imports (where `renderables.length === 1` is unreachable anyway
   * because the path lifts every metadata-indexed entity) while
   * skipping the fit on small single-entity live-create cases.
   */
  useCacheDirect: boolean;
  /**
   * The WHOLE drawing in DRAWORDER (cache-direct only) — the sequence the
   * strata plan is cut in. See `drawOrderStubs` in `runSceneBulkLoad`.
   */
  drawOrderStubs?: readonly RenderableEntity[];
}): SceneRegistries {
  const { ctx, inputs, refs, cb, handle, batched, renderables, materializedInstancedMeshes, useCacheDirect, drawOrderStubs } = args;
  const { scene, gl, invalidate, size, camera } = ctx;

  /**
   * Codex PR #784 round-8 P2 — the LIVE camera zoom (CSS px per world unit),
   * i.e. the SAME number `FrameLoops` classifies with (`ortho.zoom`). Read
   * through a getter, not captured, because the closures below outlive this
   * pass and run on later frames: the relift's PR-V5 emit-time gates and the
   * stub resolver's sub-pixel floor must both see the zoom the user is
   * actually at, not the fit-to-extents zoom the pass ran at.
   *
   * `refs.cameraStateRef` is NOT that number — it is the camera-API snapshot,
   * which a wheel-zoom does not write back on every frame (`cameraApi.ts`) —
   * so it is only the fallback for a non-orthographic / unset camera.
   */
  const liveCameraZoom = (): number => {
    const ortho = camera as THREE.OrthographicCamera;
    return ortho.isOrthographicCamera && ortho.zoom > 0
      ? ortho.zoom
      : refs.cameraStateRef.current.zoom;
  };

  // S9b — the RENDER feed additionally carries every parent's render-only
  // satellites (`renderSatellites.ts`: MLEADER block-content children and,
  // cache-direct, each INSERT `block-insert` wrapper's `childRenderables`).
  // The spatial index was already bulk-loaded from `renderables` (un-expanded)
  // by the caller, so the children render with full fidelity WITHOUT becoming
  // pickable. `renderables` still drives the auto-fit COUNT below.
  const meshFeed = expandRenderSatellites(renderables);

  // @codex PR #785 round 13, P1 — on the cache-direct branch `meshFeed` is NOT
  // the drawing (the LOD pre-classify dropped every sub-pixel stub, and Pass 2
  // relifts them on zoom-in), so the DRAWORDER strata are planned from the
  // full stub sequence instead. The legacy branch passes `undefined` and
  // `bulkLoad` keeps cutting the plan from its own feed, which there IS the
  // whole drawing.
  batched.bulkLoad(
    meshFeed,
    drawOrderStubs === undefined
      ? undefined
      : planDrawOrderStrataForDrawing({
          stubs: drawOrderStubs,
          lifted: renderables,
          resolveEntity: cb.resolveEntity,
          blocks: cb.adapterCtxBase.blocks ?? {},
          fillmode: cb.adapterCtxBase.fillmode,
        }),
  );

  // Codex P3 follow-up / Item C (2026-05-19) — populate the lifted-
  // entity tracking Set with every id the bulk-load materialised, so
  // the per-frame viewport-cull pass in `FrameLoops` can diff future
  // visible sets against this baseline. Re-built from scratch each
  // bulk-load (the prior pass's entries are stale — the bucket is
  // about to be cleared via `disposeSceneBuckets` below + the
  // upcoming `batched.bulkLoad` wrote fresh slots).
  //
  // S9b — split from the UN-EXPANDED `renderables` (the top-level
  // spatial-index entities), NOT `meshFeed`. The meshFeed additionally carries
  // the MLEADER / INSERT-wrapper satellites, which are render-only and ABSENT
  // from the spatial index. Tracking them in the lifted Set is a data-loss
  // bug: the viewport-cull diffs the lifted Set against a spatial-index-derived
  // visible set (`computeVisibleSet`), so a child (never in that set) would be
  // zero-filled on the FIRST cull frame and could never be re-lifted
  // (`liftRenderableForId` has no stub for a synthetic child id).
  // `populateLiftedAndBlockChildren` is the single choke point that keeps the
  // children OUT of the lifted Set and records them in
  // `multileaderBlockChildrenRef`, so the cull adds / removes them in lockstep
  // with their parent's own lift state (see FrameLoops Passes 1 + 2).
  populateLiftedAndBlockChildren(
    renderables,
    refs.liftedEntityIdsRef.current,
    refs.multileaderBlockChildrenRef.current,
  );
  // Codex PR #784 round 3 P1 #2 — publish the map's new contents (the
  // orchestrator may have cached its census over the cleared map meanwhile).
  refs.renderSatellitesVersionRef.current += 1;

  // ── Dispose previous meshes ────────────────────────────────────
  // Opt 8 — two consolidated Maps wrap every per-bucket /
  // per-entity resource.
  // Phase 2 Wave 2 Task 4 — also pass the InstancedMesh tracker so
  // batched-INSERT meshes from the previous pass are disposed alongside
  // the buckets.
  //
  // F1 (codex P1) dispose-ordering fix: this MUST run BEFORE the new
  // Phase 2 Wave 2 InstancedMeshes are pushed onto
  // `instancedBlockMeshesRef`. Previously the materialised new meshes
  // were attached to the scene and to the tracker first, then
  // `disposeSceneBuckets` walked the tracker and disposed the *new*
  // meshes (because the tracker is a single Array<InstancedMesh> shared
  // across passes — it doesn't snapshot the previous list). Net effect:
  // old meshes leaked + new meshes were destroyed before the first
  // paint. The dispose pass now reads only the previous pass's meshes,
  // and the new attach/push loop runs strictly after dispose.
  const registries: SceneRegistries = {
    buckets: refs.bucketsRef.current,
    entityRenders: refs.entityRendersRef.current,
    instancedBlockMeshes: refs.instancedBlockMeshesRef.current,
  };
  disposeSceneBuckets(scene, registries);

  // Phase 2 Wave 2 Task 4 — attach materialised InstancedMeshes to
  // the scene AFTER the dispose pass has cleared the previous batch.
  // Track them on the refs so the next bulkLoad / unmount disposes
  // them along with the buckets. On the cache-direct path
  // `materializedInstancedMeshes` is empty so the loop is a no-op.
  const instancedTracker = refs.instancedBlockMeshesRef.current;
  for (const item of materializedInstancedMeshes) {
    scene.add(item.mesh);
    instancedTracker.push(item.mesh);
  }
  // Bump the async atlas-build token so any in-flight loadSdfAtlas
  // promise from the previous pass sees its capture differ and skips
  // the mutation step.
  refs.textBuildTokenRef.current += 1;
  // Codex PR #784 round-11 P1 — the token bump invalidates every text build
  // of this pass; the pending-retry registry indexes exactly those runs, so
  // it is emptied in lockstep (`scene/pendingTextRetry.ts`).
  clearPendingTextRetries(refs.entityRendersRef.current);
  // PR-J — drop LOD cloud + reset snapshot
  disposeLodCloud(
    scene,
    refs.lodPointsCloudRef,
    refs.lodGroupsRef,
    refs.prevCameraSnapshotRef,
    refs.lodPointProxyCloudsRef,
  );

  // ── Build new meshes from buckets ──────────────────────────────
  // MC4 (model/paper separation) — classify every bucket by the space
  // of the entities it holds so `buildBucketMeshes` can tag each mesh
  // with `userData.spaceId`. The layout render orchestrator uses that
  // tag to pre-hide model-space line buckets on a layout sheet
  // (AutoCAD parity: model geometry shows ONLY through viewports, never
  // directly on the bare sheet). Built from `batched.getSlot`, which maps
  // each renderable id to its bucket keyId.
  //
  // Codex round 15 P1 — a bucket holds exactly ONE space: `MeshKey.space`
  // (`batchedLayer.ts`) is part of the bucket identity, so every renderable
  // landing on a given keyId agrees on the class and the repeated `set` is
  // idempotent. Before that partition existed a model line and a paper line
  // sharing layer × colour × linetype coalesced into one mesh, which had to be
  // labelled "mixed" — and a mixed bucket could not be hidden on the bare
  // sheet without hiding its paper half, so model geometry leaked onto the
  // sheet outside every viewport scissor rect.
  const spaceByBucketKeyId = new Map<MeshKeyId, BucketSpaceClass>();
  for (const renderable of meshFeed) {
    const slot = batched.getSlot(renderable.id);
    if (!slot) continue;
    spaceByBucketKeyId.set(slot.meshKey, bucketSpaceClassOf(renderable.spaceId));
  }
  // PR-LT3 — build the per-name LTYPE resolution map once per pass
  // (`{ ...coreSeed, ...document.linetypes }`, case-insensitive). The
  // material builder resolves each bucket's linetype name → dash pattern
  // against it (custom file LTYPEs override the core seed).
  const linetypeResolution = buildLinetypeResolutionMap(
    STANDARD_LINETYPES,
    inputs.linetypes,
  );
  // FIX-3 — publish the resolution map so the FrameLoops relift path can
  // build a NEW linetype bucket key with the same dash fidelity as this
  // bulk-load pass (same source: core seed + document custom LTYPEs).
  refs.linetypeResolutionRef.current = linetypeResolution;
  // FIX-1 — per-layer LTYPE map so `ByLayer` buckets (the CELTYPE-default
  // common case) resolve their dash pattern through the layer's assigned
  // linetype instead of falling to solid.
  const layerLinetypes = buildLayerLinetypeMap(
    cb.geometryDocument.styleTables?.layers,
  );
  // FIX-1 — publish so the FrameLoops relift path resolves a ByLayer bucket
  // it materialises on demand through the same per-layer map.
  refs.layerLinetypeMapRef.current = layerLinetypes;
  // PR-LT3 variant A (A2) — publish the per-pass GLOBAL dash-scale sysvars so
  // the FrameLoops relift path materialises a new bucket with the same scale.
  refs.linetypeSysvarsRef.current = inputs.linetypeSysvars;
  buildBucketMeshes({
    scene,
    gl,
    size,
    batched,
    buckets: refs.bucketsRef.current,
    spaceByBucketKeyId,
    linetypeResolution,
    layerLinetypes,
    linetypeSysvars: inputs.linetypeSysvars,
  });

  // PR-D — emit hatch + wipeout fill meshes.
  const renderableById = new Map<string, RenderableEntity>();
  for (const r of meshFeed) renderableById.set(r.id, r);
  // Opt 3 — spatial index stores only ids now; reattach the latest
  // `renderableById` map as its entity resolver so hit-test / window-
  // select / OSNAP / LOD viewport-cull all see this pass's full
  // renderables when they resolve ids returned by `intersect()` /
  // `pick()`. The bulkLoad above already replaced the bbox tree; this
  // closes the loop on the resolver side.
  //
  // Codex PR #784 round-8 P2 (`lod/lodPolicy.ts:217`) — on the cache-direct
  // path the map alone is NOT the whole answer: it holds only the stubs the
  // LOD pre-classify lifted, so every sub-pixel-at-fit id resolved to
  // `undefined` and `computeVisibleSet` dropped it — the per-frame relift
  // could never reach it, which is the ONLY reason `isAlwaysLiftedStubType`
  // had to exempt whole kinds (and exempting `Block` made this pass expand
  // every INSERT's full child tree). Chain the METADATA-STUB FALLBACK behind
  // the map (`createMetadataStubResolver`, built on demand, nothing retained)
  // so a never-lifted id still resolves — by its real bbox — and reaches Pass
  // 2's on-demand lift. `entityResolverRef` below publishes the REAL renderable
  // back into this map as soon as Pass 2 lifts one, so the fallback answers
  // only for ids that have never been drawn.
  //
  // Legacy (small-drawing) path keeps the map-only resolver: it lifts every
  // entity up front and has no metadata stubs behind it.
  //
  // Codex PR #784 round-11 P2 — `proxyStubResolver` is the SAME helper without
  // that floor. It answers only the NON-EXPANDING proxy resolve
  // (`EntityLiftRequest.proxyOnly`), which `FrameLoops` uses to put one
  // points-cloud dot at an id a paper-space VIEWPORT renders in the 0.5-2 px
  // band. Those ids are measured at the VIEWPORT's scale, so the main-camera
  // floor — whose whole purpose is to stop `computeVisibleSet` allocating a
  // stub for every sub-pixel id in the MAIN rect — would withhold exactly the
  // ids that still need a dot. Keeping them as two closures is what lets the
  // hot path keep its floor while the bounded, viewport-named set bypasses it.
  let proxyStubResolver: ((id: string) => RenderableEntity | undefined) | null =
    null;
  if (useCacheDirect) {
    const metadataIndex = getEntityMetadataIndex();
    const getStubBounds = (id: string): ReturnType<
      typeof handle.spatialIndex.getBounds
    > => handle.spatialIndex.getBounds(id);
    proxyStubResolver = createMetadataStubResolver({
      metadataIndex,
      getBounds: getStubBounds,
    });
    const stubFallback = createMetadataStubResolver({
      metadataIndex,
      getBounds: getStubBounds,
      // Sub-pixel floor — see the helper's `cameraZoom` doc. `computeVisibleSet`
      // resolves every rbush hit on every camera change, so without it a
      // fit-to-extents frame on a huge drawing would allocate a throw-away stub
      // for every never-lifted id in view.
      cameraZoom: liveCameraZoom,
    });
    handle.spatialIndex.setEntityResolver(
      (id) => renderableById.get(id) ?? stubFallback(id),
    );
  } else {
    handle.spatialIndex.setEntityResolver((id) => renderableById.get(id));
  }
  const v5Zoom = refs.cameraStateRef.current.zoom;
  emitFillAndArrayMeshes(
    scene,
    meshFeed,
    renderableById,
    refs.entityRendersRef.current,
    v5Zoom,
    refs.hiddenLayersRef.current,
    // Codex PR #785 round 12 P1 — the band fill is ordered against the line
    // buckets through the plan `batched.bulkLoad(meshFeed)` just cut from the
    // SAME feed, so the fill and the buckets read one DRAWORDER sequence.
    batched.getDrawOrderStrata(),
  );

  // PR-E2 — text + mtext rendering.
  // @codex #334 (P2-B) — build the STYLE name → fontName lookup so a
  // generically-named STYLE (`name=Standard, fontName=gost.shx`)
  // dispatches on its actual font file. Keys are lowercased style names;
  // entries without a `fontName` are skipped (the lookup miss falls back
  // to the styleName in `emitTextMeshes`, the pre-fix dispatch input).
  const styleFontByName = buildStyleFontMap(inputs.textStyles);
  emitTextMeshes(
    scene,
    meshFeed,
    refs.entityRendersRef.current,
    refs.textBuildTokenRef,
    v5Zoom,
    invalidate,
    styleFontByName,
    // FIX (Codex S9b P2) — pass the REF, not `.current`: text builds async and
    // `applyAndMirrorLayerVisibility` (end-of-pass, below) REPLACES the ref's
    // Set, so the resolution-time read must see the live value.
    refs.hiddenLayersRef,
  );

  // Codex P3 follow-up / Item C — wire the on-demand resolver bound to this
  // bulk-load's cache + buildPreview + adapterCtxBase. The FrameLoops
  // viewport-cull pass (Pass 2) calls it to lift a newly-visible entity that
  // this pass dropped at LOD pre-classify time, or that a prior frame culled.
  // Returns `null` when the id is unknown to the cache or the lift rejects the
  // payload. An INSERT resolves to its `block-insert` wrapper whose
  // `childRenderables` Pass 2 re-expands as satellites.
  //
  // Codex PR #784 round-8 P2 — the lift is no longer the WHOLE relift. Two
  // steps make a first-time lift indistinguishable from a bulk-load lift, so
  // the `Block` (and, in principle, TEXT / DIMENSION / MULTILEADER) exemption
  // in `isAlwaysLiftedStubType` is no longer load-bearing:
  //
  //   1. PUBLISH — the freshly lifted parent AND its render-only satellites go
  //      into `renderableById`, this pass's spatial-index resolver map. Pick /
  //      window-select / OSNAP / the layer helpers then see the REAL geometry
  //      for an id that was never bulk-lifted (before, they saw the metadata
  //      stub and, before the fallback above, nothing at all). Retention is
  //      bounded by "ids that have actually been drawn", which is what the
  //      always-lift exemption paid UP FRONT for every INSERT in the drawing.
  //   2. EMIT — `emitReliftDirectMeshes` builds the direct meshes
  //      `BatchedLayer.addEntity` cannot: fills, arrow heads, glyphs, array
  //      instances, for the parent and every satellite. Without it a
  //      first-time-lifted reference would come back from zoom as bare lines.
  //      Idempotent (see the helper) so a cull → relift cycle never
  //      double-emits, and it reads the LIVE camera zoom for the PR-V5
  //      emit-time gates.
  //
  // Both steps run inside the resolver — not in `FrameLoops` — because this
  // closure is the one place that already holds the pass's scene, entity-render
  // registry, text build token, style-font map and `renderableById`.
  if (useCacheDirect) {
    const cache = refs.visibleSetCacheRef.current;
    if (cache) {
      const cacheCapture = cache;
      const buildPreviewCapture = cb.buildPreview;
      const resolveEntityCapture = cb.resolveEntity;
      const adapterCtxCapture = cb.adapterCtxBase;
      const proxyCapture = proxyStubResolver;
      refs.entityResolverRef.current = (
        id: string,
        request?: EntityLiftRequest,
      ): RenderableEntity | null => {
        try {
          // Codex PR #784 round-11 P2 — the NON-EXPANDING proxy resolve. Answer
          // with the metadata stub (real bounds + resolved colour) and stop:
          // nothing is lifted, nothing is published into `renderableById` (a
          // stub there would SHADOW a later real lift for pick / OSNAP), and no
          // mesh is emitted. It is what a viewport's point-band ids get.
          if (request?.proxyOnly === true) {
            return proxyCapture?.(id) ?? null;
          }
          const lifted = liftRenderableForId({
            id,
            cache: cacheCapture,
            buildPreview: buildPreviewCapture,
            resolveEntity: resolveEntityCapture,
            ctx: adapterCtxCapture,
          });
          if (!lifted) return null;
          // Same expansion the bulk-load feed uses — parent first, then its
          // render-only satellites (one truth: `renderSatellites.ts`).
          const reliftFeed = expandRenderSatellites([lifted]);
          for (const r of reliftFeed) renderableById.set(r.id, r);
          emitReliftDirectMeshes({
            scene,
            feed: reliftFeed,
            renderableById,
            entityRenders: refs.entityRendersRef.current,
            textBuildTokenRef: refs.textBuildTokenRef,
            // LIVE zoom — the camera has moved since this pass ran; that move
            // is what triggered the relift. A relifted block-content TEXT child
            // must clear `shouldSkipTextRun` at the CURRENT zoom, not at the
            // fit-to-extents zoom that would skip every glyph.
            //
            // Codex PR #784 round-11 P1 — …and not at the MAIN camera's zoom
            // either, when the lift was requested by a paper-space VIEWPORT
            // that magnifies this id: `EntityLiftRequest.emitScale` carries the
            // largest requesting viewport's model scale
            // (`ViewportLiftRequests.lift`), and the glyph must be built for
            // the camera that will RENDER it. The maximum is the safe
            // reduction — a mesh built for the larger scale is still correct at
            // the smaller one, while the reverse leaves the label absent inside
            // the viewport with no second resolver call to fix it (the parent
            // is lifted by then).
            v5Zoom: Math.max(liveCameraZoom(), request?.emitScale ?? 0),
            invalidate,
            styleFontByName,
            hiddenLayersRef: refs.hiddenLayersRef,
            // Codex PR #785 round 12 P1 — the same plan the bucket meshes
            // read. `BatchedLayer.addEntity` (which this relift also drives)
            // resolves the lifted entity's stratum through it too, so its
            // silhouette lands in the bucket whose `renderOrder` sits just
            // above the fill emitted here.
            drawOrderStrata: batched.getDrawOrderStrata(),
          });
          return lifted;
        } catch {
          return null;
        }
      };
    } else {
      refs.entityResolverRef.current = null;
    }
  } else {
    // Synchronous-path (small drawing) — resolver not wired because
    // the bulk-load already lifted every entity; per-frame cull does
    // not need an on-demand resolver. Clear any prior resolver so a
    // stale closure can't leak.
    refs.entityResolverRef.current = null;
  }

  // Build the entity → texture-slot lookup so setSelection / hover
  // can find the right bucket+ordinal in O(1) without iterating
  // buckets every time. Opt 8 — slot lives on the EntityRender entry.
  for (const renderable of meshFeed) {
    const slot = batched.getSlot(renderable.id);
    if (slot) {
      const existing = refs.entityRendersRef.current.get(renderable.id);
      if (existing) {
        existing.textureSlot = {
          keyId: slot.meshKey,
          instanceIndex: slot.instanceIndex,
        };
      } else {
        refs.entityRendersRef.current.set(renderable.id, {
          textureSlot: {
            keyId: slot.meshKey,
            instanceIndex: slot.instanceIndex,
          },
        });
      }
    }
  }

  // FIX (Codex S9b P2 — "Mirror selection slots to MLEADER block children" +
  // round-7 P3 — "Record satellite slots without requiring a parent slot"): fan
  // each MLEADER's block-content child slots onto the parent's `satelliteSlots`
  // so selecting / hovering the callout lights its embedded block graphics too,
  // not just the leader line. The parent render is UPSERTED so a content-only
  // (`leaderType=0`) callout — which emits no parent slot of its own — still
  // attaches its satellites and stays selectable. See the helper's JSDoc.
  assignMultileaderSatelliteSlots(
    refs.entityRendersRef.current,
    refs.multileaderBlockChildrenRef.current,
  );

  // PR-B placeholder fit-view: position the OrthographicCamera so
  // freshly-imported entities are inside the frustum.
  //
  // FIX (camera auto-reset bug): never re-fit on a pass that merely
  // edits the CURRENT drawing. Re-fitting on every pass clobbered
  // `ortho.position` / frustum / `ortho.zoom = 1`, wiping wheel-zoom
  // state and snapping the camera back to the bounds centre after every
  // command. AutoCAD never auto-zooms when an entity is added to an open
  // drawing — fit-to-extents is an explicit ZOOM E.
  //
  // Issue #225 — auto-fit once PER DOCUMENT, not once per SceneContent
  // lifetime. The pre-#225 gate latched on a lifetime one-shot
  // (`cameraEverInitializedRef`, flipped on the first fit and never
  // reset). Because `<SceneContent>` has no doc-keyed React `key`, it
  // persists across document loads, so the latch stayed `true` after the
  // first drawing and the fit block was skipped for every drawing opened
  // afterwards — the second document rendered off-screen until the user
  // ran ZOOM E. `shouldAutoFitCamera` keys on the document claim instead
  // (`cameraFitDocStateRef`): a NEW import / "new drawing" produces a
  // fresh id → re-fit; an edit preserves the id → no re-fit.
  //
  // Sub-gate `(useCacheDirect || renderables.length > 1)` is UNCHANGED —
  // re-introducing the PR-F2 amendment that was rolled back in d82e913.
  // The rollback's rationale ("1-entity DXF leaves camera unset")
  // underweighted the live-create UX cost: drawing line 1 on an empty
  // stage tripped fit-to-extents → camera zoomed tight on line 1's
  // bounds → line 2 drawn outside the tight frustum was invisible
  // (entity present in scene + spatial index, but off-screen). Confirmed
  // via repro 2026-05-18 (scripts/repro-two-lines.mjs): after-line1
  // cameraWorldRect=(24, 375, 90, 310); after-line2 unchanged; line 2 at
  // (500,500)→(700,700) outside frustum. This sub-gate is keyed on the
  // renderable COUNT, not the document id, so a live edit that adds a
  // 2nd entity to the SAME drawing still does NOT re-fit (the id is
  // unchanged) AND a single live-drawn entity still does not trip a
  // tight fit.
  //
  // Trade-off (unchanged):
  //   - useCacheDirect path always fits (huge imports stay framed)
  //   - renderables.length > 1 fits (multi-entity import + 2nd
  //     live-created entity both work)
  //   - renderables.length === 1 + small drawing path: no fit. The
  //     default OrthographicCamera frustum (~±478×±300) covers the
  //     common origin-anchored cases; for off-axis single-entity
  //     small DXF imports the user invokes ZOOM E. AutoCAD ZE is
  //     explicit anyway, so this matches parity better than the
  //     reverted auto-fit-on-first-entity behaviour.
  //
  // #235 P2 (reset-then-draw): the document claim advances on EVERY pass
  // right after this block (not only inside it). Without that, the
  // sequence open-A (fit, claim uuid-A) → Reset (constant id, count 0 →
  // no fit, claim stays uuid-A) → draw 2 entities (constant id ≠ uuid-A)
  // would FIRE a fit and snap zoom-to-extents as you draw into a
  // fresh/empty drawing — which AutoCAD never does. Claiming the id on
  // every pass makes the post-reset empty pass record the constant id,
  // so the subsequent same-id draw compares equal and no fit fires.
  //
  // #235 follow-up (@codex P2, streamed OPEN): the claim carries a
  // `contentSeen` flag so the every-pass claim above cannot eat a
  // streamed document's one fit. A streaming OPEN commits the new
  // `geometryDocument` during the sync phase and rebuilds
  // `entityMetadataIndex` afterwards, so this effect runs once with the
  // new id but ZERO renderables; that pre-pass claims the id with
  // `contentSeen: false`, and when the index bump re-runs the effect the
  // cache-direct pass that actually materialises the content still fits
  // (`useCacheDirect && !contentSeen` clause in `shouldAutoFitCamera`).
  // Pre-fix, the bare-id claim made the content pass compare equal and
  // skip — the second streamed file stayed off-screen until ZOOM E.
  //
  // #235 follow-up (@codex P2, start-from-empty): the pre-fix predicate
  // also OR-ed a `!everInitialized` lifetime-latch clause. In a session
  // where no fit ever ran (fresh empty drawing), the empty pass and the
  // first drawn entity claimed the seed id without setting the latch, so
  // drawing the 2nd entity (count > 1, same id) still forced a fit
  // through the OR-branch — the camera snapped mid-drawing. The clause
  // was redundant for the legitimate first-ever fit (a never-claimed /
  // different id already fires the per-document clause), so it — and the
  // now reader-less `cameraEverInitializedRef` latch — were removed.
  if (
    shouldAutoFitCamera({
      lastFitDocState: refs.cameraFitDocStateRef.current,
      currentDocumentId: cb.geometryDocument.id,
      renderableCount: renderables.length,
      useCacheDirect,
    })
  ) {
    // AutoCAD parity: when the source DXF has a VPORT *Active saved
    // view, apply that framing exactly instead of computing a zoom-
    // extents box with margin. Falls back to the AABB fit when no
    // saved view is present OR when the helper rejects the savedView
    // (non-plan viewDirection, non-finite / non-positive values). The
    // helper returns `false` in those rejection cases — without this
    // fallback the camera would be left at its default seed
    // `(0, 0, 100) zoom=1` and the user would see no content.
    let applied = false;
    if (cb.geometryDocument.savedView) {
      applied = applySavedViewToOrthographicCamera(
        camera,
        cb.geometryDocument.savedView,
        size.width,
        size.height,
        getCoordinateOrigin(),
      );
    }
    if (!applied) {
      fitOrthographicCameraToRenderables(
        camera,
        renderables,
        size.width,
        size.height,
      );
    }
  }

  // Advance the per-document claim on every bulk-load — even when no fit ran
  // (empty/new doc, or the live-create count sub-gate blocked it). Otherwise a
  // freshly reset/new EMPTY drawing keeps the PRIOR document's claim in
  // `cameraFitDocStateRef`, and the first time content is drawn into it the
  // per-document clause (`currentId !== claimed id`) would auto-zoom — AutoCAD
  // never auto-zooms while you draw into a new drawing (#235 P2). The
  // transition (`claimAutoFitDocument`) keeps `contentSeen: false` while every
  // pass for the claimed id is empty, so a streamed OPEN's 0-renderable
  // pre-pass leaves the document's one fit armed for the cache-direct content
  // pass (#235 follow-up @codex P2); once a pass has content the flag latches
  // on and later same-id passes never re-fit.
  //
  // Placement matters: this runs AFTER the fit `if` block above, which read
  // the PRE-claim `cameraFitDocStateRef.current` as `lastFitDocState` (the
  // gate input). So the gate still sees the previous claim; only after the
  // gate has decided do we advance the ref to the current document.
  refs.cameraFitDocStateRef.current = claimAutoFitDocument(
    refs.cameraFitDocStateRef.current,
    cb.geometryDocument.id,
    renderables.length,
  );

  // PR-I — apply layer visibility / lock / freeze to the freshly built
  // buckets so meshes inherit current layer state for the first frame.
  // F4 (codex P1 #4) — also pass the InstancedMesh tracker so batched-
  // INSERT meshes inherit current hidden-layer state on the first frame
  // (without this, hiding a layer containing batched INSERTs left them
  // visible until the next full bulkLoad).
  applyAndMirrorLayerVisibility({
    buckets: refs.bucketsRef.current,
    entityRenders: refs.entityRendersRef.current,
    instancedBlockMeshes: refs.instancedBlockMeshesRef.current,
    layerStates: inputs.layerStates,
    lockedLayersRef: refs.lockedLayersRef,
    hiddenLayersRef: refs.hiddenLayersRef,
    sharedLockedLayersRef: refs.sharedLockedLayersRef,
  });

  // fix/selection-highlight-not-applied — re-derive the per-instance highlight
  // byte onto the buckets this pass just built.
  //
  // `disposeSceneBuckets` above disposed EVERY previous `SelectionStateTexture`
  // and cleared `entityRenders`; `buildBucketMeshes` then allocated brand-new
  // all-zero ("normal") textures and the `batched.getSlot` loop re-registered
  // every `textureSlot`. So at this point the renderer holds no record at all of
  // what is selected — while `state.selection.entityIds` is unchanged and still
  // names the entity.
  //
  // A bulk-load is triggered by the ENTITY feed / layer table / linetype table /
  // canvas size, never by a selection change, so the standalone selection-sync
  // effect (`useSceneEffects` effect 3) does NOT re-run in that commit: it is
  // dep-gated on `selectedEntityIds` IDENTITY. Nothing else writes the byte, so
  // pre-fix the highlight was dropped permanently — re-selecting the SAME entity
  // does not change the array identity either, which is exactly why the observed
  // fault never recovered within a session.
  //
  // Mirrors the two re-derivations this pass already performs for the same
  // reason — `applyAndMirrorLayerVisibility` directly above (hidden/locked/frozen
  // state onto the fresh handles) and `FrameLoops`' post-relift re-stamp — and
  // routes through the SAME slot-fan one-truth so same-bucket satellites light up
  // identically. Called BEFORE `invalidate()` so the first painted frame already
  // carries the correct bytes.
  restampHighlightState({
    entityRenders: refs.entityRendersRef.current,
    buckets: refs.bucketsRef.current,
    selectedEntityIds: refs.selectedEntityIdsRef.current,
  });

  // demand frameloop: nothing else schedules a paint, so we have to
  // explicitly request one once the new buckets are in the scene.
  invalidate();

  return registries;
}

// `disposePointsCloud` re-export so direct callers don't have to know
// about the lod barrel.
export { disposePointsCloud };
